/**
 * Shared responsive layout values.
 *
 * The flow is a single centred column. On phones it fills the screen (capped at
 * 430px, the original fixed width). On tablets and desktops it widens a little so
 * the extra space is used, but stays narrow enough for forms and legal copy to
 * read comfortably.
 *
 * Breakpoints are the Zumo DS set (see BREAKPOINTS below): xs 0+, sm 640+,
 * md 768+, lg 1024+, xl 1280+. The MUI theme is configured to the same values.
 */
/**
 * Breakpoints, as the Zumo DS defines them (brand.json → breakpoint). The MUI
 * theme reads these so `sm` / `md` in sx props and the theme's media queries
 * all mean the same widths. These are the only widths that may appear in a
 * media query.
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const

export const CONTENT_MAX_WIDTH = {
  xs: '430px',
  sm: '600px',
  md: '720px',
} as const

/**
 * Width of the screen illustrations (ZIcon). 9rem is the original phone size;
 * it steps up once at the tablet breakpoint and is capped there, so it doesn't
 * dominate a desktop screen.
 */
export const ILLUSTRATION_WIDTH = {
  xs: '9rem',
  sm: '11rem',
} as const

/** Horizontal page padding, in sync with the width steps above. */
export const CONTENT_PADDING_X = {
  xs: '5%',
  sm: '2rem',
  md: '2.5rem',
} as const

/**
 * Vertical spacing scale, as the Zumo DS defines it (brand.json → space).
 * Below 16px step by 2 (dense UI); from 16px step by 8 (layout rhythm).
 * Odd values in between are strays. Horizontal insets are set per
 * component and are not governed by this scale.
 */
export const SPACE = {
  '3xs': '2px',
  '2xs': '4px',
  xs: '6px',
  sm: '8px',
  md: '10px',
  lg: '12px',
  xl: '16px',
  '2xl': '24px',
  '3xl': '32px',
  '4xl': '40px',
  '5xl': '48px',
  '6xl': '72px',
  '7xl': '96px',
} as const

/**
 * Largest gap allowed between a screen's content and its action buttons on
 * tablet and desktop. Below this the buttons stay at the bottom as on phones;
 * beyond it they come up to sit at the cap. Phones have no cap.
 */
export const ACTION_GAP_MAX = SPACE['5xl']

/** Corner radius for card-like surfaces (choice cards, the declaration box). DS radius.lg. */
export const CARD_RADIUS = 12
